import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, CheckCircle2, XCircle, CalendarCheck, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { getReservationData, confirmEarlyAccessBooking } from '../api/reservations';

const ConfirmEarlyAccessPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['earlyAccessReservation', token],
    queryFn: () => getReservationData(token!),
    enabled: !!token,
    retry: false,
    meta: { silentOn: [400, 401, 403, 404, 410, 422], source: 'confirm-early-access-link' },
  });

  const handleConfirm = async () => {
    if (!token) return;
    setConfirming(true);
    setConfirmError(false);
    try {
      await confirmEarlyAccessBooking(token);
      setConfirmed(true);
    } catch {
      setConfirmError(true);
    } finally {
      setConfirming(false);
    }
  };

  const renderLoading = () => (
    <div className="text-center text-foreground/40 text-sm py-12">
      {t('CONFIRM_EARLY_ACCESS_LOADING')}
    </div>
  );

  const renderNoToken = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="text-red-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('CONFIRM_EARLY_ACCESS_INVALID_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('CONFIRM_EARLY_ACCESS_INVALID_SUBTITLE')}</p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_EARLY_ACCESS_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="text-red-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('CONFIRM_EARLY_ACCESS_INVALID_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('CONFIRM_EARLY_ACCESS_INVALID_SUBTITLE')}</p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_EARLY_ACCESS_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderCancelled = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
          <XCircle className="text-orange-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('CONFIRM_EARLY_ACCESS_CANCELLED_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('CONFIRM_EARLY_ACCESS_CANCELLED_MESSAGE')}</p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_EARLY_ACCESS_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderConfirmed = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('CONFIRM_EARLY_ACCESS_CONFIRMED_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('CONFIRM_EARLY_ACCESS_CONFIRMED_SUBTITLE')}</p>
      </div>
      {data && (
        <div className="w-full max-w-sm px-4 space-y-4">
          <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
            <SummaryRow label={t('CONFIRM_EARLY_ACCESS_RESTAURANT')} value={data.restaurant.name} />
            <SummaryRow label={t('CONFIRM_EARLY_ACCESS_DATE')} value={data.date} />
            <SummaryRow label={t('CONFIRM_EARLY_ACCESS_TIME')} value={data.time} />
            <SummaryRow label={t('CONFIRM_EARLY_ACCESS_GUESTS')} value={data.partySize} />
          </div>
        </div>
      )}
      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_EARLY_ACCESS_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderReservation = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mb-6">
          <CalendarCheck className="text-brand" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('CONFIRM_EARLY_ACCESS_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('CONFIRM_EARLY_ACCESS_SUBTITLE')}</p>
      </div>

      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          <SummaryRow label={t('CONFIRM_EARLY_ACCESS_RESTAURANT')} value={data!.restaurant.name} />
          <SummaryRow label={t('CONFIRM_EARLY_ACCESS_DATE')} value={data!.date} />
          <SummaryRow label={t('CONFIRM_EARLY_ACCESS_TIME')} value={data!.time} />
          <SummaryRow label={t('CONFIRM_EARLY_ACCESS_GUESTS')} value={data!.partySize} />
        </div>
      </div>

      {confirmError && (
        <p className="text-red-500 text-sm mt-4 text-center px-4">
          {t('CONFIRM_EARLY_ACCESS_ERROR')}
        </p>
      )}

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleConfirm}
          disabled={confirming}
        >
          {confirming ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" />
              {t('CONFIRM_EARLY_ACCESS_CONFIRMING')}
            </>
          ) : (
            <>
              <CheckCircle2 size={18} className="mr-2" />
              {t('CONFIRM_EARLY_ACCESS_CONFIRM_RESERVATION_BUTTON')}
            </>
          )}
        </Button>
      </div>
    </>
  );

  const isAlreadyConfirmed = data && data.status === 'confirmed';
  const isCancelled = data && data.cancelled;
  const isValid = data && !isCancelled && !isAlreadyConfirmed;

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {!token && renderNoToken()}
          {token && isLoading && renderLoading()}
          {token && error && renderError()}
          {token && isCancelled && renderCancelled()}
          {token && confirmed && renderConfirmed()}
          {token && !confirmed && isAlreadyConfirmed && renderConfirmed()}
          {token && !confirmed && isValid && renderReservation()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ConfirmEarlyAccessPage;
